import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface HarvestRecommendationItem {
  partName: string;
  partCategory: string;
  brandPrice: number | null;
  resalePrice: number | null;
  /** Parts Register Technology (column E); 'all' when not technology-specific. */
  technology?: "all" | "heat_pump" | "condenser" | "vented";
}

export interface HarvestRecommendationsPayload {
  sheetVersion: string;
  sheetId: string;
  sheetNotes?: string | null;
  sheetFilename?: string | null;
  matchedOn: { category: string; technology: string; brand: string; brandValuePct?: number; /** FIX-286: null = unknown. */ brandRecognised?: boolean | null };
  thresholds: {
    min_demand: string;
    min_priority: number;
    min_price_eur: number;
    resale_price_pct: number;
  };
  items: HarvestRecommendationItem[];
}

type HarvestRecommendationRequest = {
  applianceId?: string | null;
  companyId?: string | null;
  category?: string | null;
  technology?: string | null;
  brand?: string | null;
};

export function useHarvestRecommendations(input?: string | null | HarvestRecommendationRequest) {
  const request: HarvestRecommendationRequest =
    input && typeof input === "object"
      ? input
      : { applianceId: typeof input === "string" ? input : null };
  const isUuid = (v?: string | null) => !!v && /^[0-9a-f-]{36}$/i.test(v);
  const enabled = Boolean(
    isUuid(request.applianceId) || (request.companyId && (request.category || request.technology || request.brand)),
  );

  return useQuery<HarvestRecommendationsPayload | null>({
    queryKey: ["harvest_recommendations", request],
    enabled,
    queryFn: async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      const { data, error } = await supabase.functions.invoke("harvest-recommendations", {
        body: request,
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      });
      if (error) {
        // 404 (no sheet) returns null payload from caller's perspective
        const msg = (error as any)?.message || "";
        if (/No active recommendation sheet/i.test(msg)) return null;
        throw error;
      }
      return data as HarvestRecommendationsPayload;
    },
    staleTime: 30 * 1000,
    refetchOnMount: "always",
    retry: false,
  });
}
